<template>
  <template v-if="editor">
    <BubbleMenu :editor="editor" />
    <DragHandle :editor="editor" />
  </template>

  <editor-content :editor="editor" />
</template>

<script setup lang="ts">
  import DragHandlePlugin from "@tiptap-pro/extension-drag-handle";
  import NodeRange from "@tiptap-pro/extension-node-range";
  import BubbleMenu from "./components/BubbleMenu.vue";
  import DragHandle from "./components/DragHandle.vue";
  import { Underline } from "@tiptap/extension-underline";
  import StarterKit from "@tiptap/starter-kit";
  import { Editor, EditorContent, useEditor } from "@tiptap/vue-3";
  import type { ShallowRef } from "vue";
  import { useVariableExtension } from "./extensions/variable";
  import { useFileHandlerExtension } from "./extensions/file-handler";
  import ImageExtension from "./extensions/image";
  import Placeholder from "@tiptap/extension-placeholder";

  const VariableExtension = await useVariableExtension();
  const FileHandlerExtension = useFileHandlerExtension();

  const model = defineModel<string | null>({
    default: null,
  });

  const editor: ShallowRef<Editor | undefined> = useEditor({
    extensions: [
      StarterKit,
      NodeRange.configure({
        key: null,
      }),
      DragHandlePlugin,
      Underline,
      VariableExtension,
      FileHandlerExtension,
      ImageExtension,
      Placeholder.configure({
        placeholder: "Write something …",
      }),
    ],
    content: model.value,
    onUpdate: ({ editor }) => {
      model.value = editor.getHTML();
    },
  });

  watch(model, (value) => {
    const isSame = editor.value?.getHTML() === value;

    if (isSame) {
      return;
    }

    editor.value?.commands.setContent(value, false);
  });
</script>

<style lang="scss">
  ::selection {
    background-color: #70cff850;
  }

  .ProseMirror {
    appearance: none;
    outline: none;
    border: none;

    > * {
      margin-left: 6rem !important;
    }

    .ProseMirror-widget * {
      margin-top: auto;
    }
  }

  .ProseMirror-noderangeselection {
    *::selection {
      background: transparent;
    }

    * {
      caret-color: transparent;
    }
  }

  .ProseMirror-selectednode,
  .ProseMirror-selectednoderange {
    position: relative;

    &::before {
      position: absolute;
      pointer-events: none;
      z-index: -1;
      content: "";
      top: -0.25rem;
      left: -0.25rem;
      right: -0.25rem;
      bottom: -0.25rem;
      background-color: #70cff850;
      border-radius: 0.2rem;
    }
  }

  .tiptap p.is-editor-empty:first-child::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .variable {
  }
</style>
